/**
 * Spark Enhanced Authentication Types
 *
 * Types for enhanced Spark authentication with:
 * - Bound actions (transfer, sign, approve)
 * - Cross-device linking (desktop-mobile)
 * - Authentication levels (biometric, PIN, multisig)
 * - Replay protection via NonceRegistry
 *
 * All crypto operations happen in Rust WASM - TypeScript only handles types.
 */
// ============================================================================
// Authentication Levels
// ============================================================================
/** Authentication level required for an action */
export var AuthLevel;
(function (AuthLevel) {
    /** No authentication required (just scan) */
    AuthLevel[AuthLevel["None"] = 0] = "None";
    /** Biometric only (Face ID / Fingerprint) */
    AuthLevel[AuthLevel["Biometric"] = 1] = "Biometric";
    /** Biometric + 6-digit PIN */
    AuthLevel[AuthLevel["BiometricPin"] = 2] = "BiometricPin";
    /** Biometric + PIN + time delay */
    AuthLevel[AuthLevel["BiometricPinDelay"] = 3] = "BiometricPinDelay";
    /** Multi-signature required */
    AuthLevel[AuthLevel["Multisig"] = 4] = "Multisig";
})(AuthLevel || (AuthLevel = {}));
/** Action encoding size (256 bytes) */
export const ACTION_ENCODING_SIZE = 256;
/** Maximum payload size (249 bytes) */
export const MAX_PAYLOAD_SIZE = 249;
// ============================================================================
// Well-Known Namespaces
// ============================================================================
export const NAMESPACES = {
    ESTREAM_CONSOLE: 'dev.estream.console',
    ESTREAM_GOVERNANCE: 'dev.estream.governance',
    TAKETITLE: 'io.taketitle',
    POLYMESSENGER: 'app.polymessenger',
    TRUERESOLVE: 'app.trueresolve',
};
// ============================================================================
// Well-Known Action IDs
// ============================================================================
export const CONSOLE_ACTIONS = {
    LOGIN: 0x0001,
    ADMIN_ACTION: 0x0002,
    DEPLOY_CIRCUIT: 0x0010,
    UPDATE_CIRCUIT: 0x0011,
    PAUSE_CIRCUIT: 0x0012,
    RESUME_CIRCUIT: 0x0013,
};
export const GOVERNANCE_ACTIONS = {
    VOTE: 0x0001,
    STAKE: 0x0002,
    UNSTAKE: 0x0003,
    CLAIM_REWARDS: 0x0004,
    CREATE_PROPOSAL: 0x0010,
    DELEGATE: 0x0020,
    UNDELEGATE: 0x0021,
};
export const TAKETITLE_ACTIONS = {
    INVEST: 0x0001,
    TRANSFER: 0x0002,
    CLAIM_DIVIDEND: 0x0003,
    SIGN_AGREEMENT: 0x0004,
    LIST_FOR_SALE: 0x0005,
    CANCEL_LISTING: 0x0006,
    CONNECT: 0x0010,
    MINT_NFT: 0x0020,
};
export const POLY_ACTIONS = {
    CONNECT: 0x0001,
    VERIFY_CONTACT: 0x0002,
    BLIND_CONNECT: 0x0003,
    ADD_GUARDIAN: 0x0004,
    GUARDIAN_APPROVE: 0x0005,
    EXPORT_BACKUP: 0x0006,
    LINK_DEVICE: 0x0007,
    REVOKE_DEVICE: 0x0008,
    START_CALL: 0x0010,
    JOIN_GROUP: 0x0020,
};
// ============================================================================
// Helper Functions
// ============================================================================
/**
 * Check if an auth level requires biometric authentication
 */
export function requiresBiometric(level) {
    return level >= AuthLevel.Biometric;
}
/**
 * Check if an auth level requires PIN
 */
export function requiresPin(level) {
    return level === AuthLevel.BiometricPin || level === AuthLevel.BiometricPinDelay;
}
/**
 * Get auth level from string or number
 */
export function parseAuthLevel(value) {
    if (typeof value === 'number') {
        return value;
    }
    switch (value.toLowerCase()) {
        case 'none': return AuthLevel.None;
        case 'biometric': return AuthLevel.Biometric;
        case 'biometric_pin': return AuthLevel.BiometricPin;
        case 'biometric_pin_delay': return AuthLevel.BiometricPinDelay;
        case 'multisig': return AuthLevel.Multisig;
        default: throw new Error(`Unknown auth level: ${value}`);
    }
}
/**
 * Get human-readable name for auth level
 */
export function authLevelName(level) {
    switch (level) {
        case AuthLevel.None: return 'None';
        case AuthLevel.Biometric: return 'Biometric';
        case AuthLevel.BiometricPin: return 'Biometric + PIN';
        case AuthLevel.BiometricPinDelay: return 'Biometric + PIN + Delay';
        case AuthLevel.Multisig: return 'Multi-Signature';
        default: return 'Unknown';
    }
}
// ============================================================================
// Shared Spark Lattice Configuration
// ============================================================================
/**
 * Default Spark lattice URL (production).
 * Override with VITE_SPARK_LATTICE_URL or SPARK_LATTICE_URL env var.
 */
export const DEFAULT_SPARK_LATTICE_URL = 'https://edge.estream.dev';
/**
 * Get the configured Spark lattice URL.
 *
 * Priority:
 * 1. VITE_SPARK_LATTICE_URL (Vite apps)
 * 2. SPARK_LATTICE_URL (Node.js/Workers)
 * 3. Default: https://edge.estream.dev
 *
 * For local development, set:
 * - VITE_SPARK_LATTICE_URL=http://localhost:8787
 */
export function getSparkLatticeUrl() {
    // Check Vite env first (browser builds)
    if (typeof import.meta !== 'undefined' && import.meta.env) {
        const viteUrl = import.meta.env.VITE_SPARK_LATTICE_URL;
        if (viteUrl)
            return viteUrl;
    }
    // Check Node.js/Worker env
    if (typeof process !== 'undefined' && process.env) {
        const nodeUrl = process.env.SPARK_LATTICE_URL;
        if (nodeUrl)
            return nodeUrl;
    }
    // Check global config (for runtime override)
    if (typeof globalThis !== 'undefined' && globalThis.__SPARK_LATTICE_URL__) {
        return globalThis.__SPARK_LATTICE_URL__;
    }
    return DEFAULT_SPARK_LATTICE_URL;
}
/**
 * Set the Spark lattice URL at runtime.
 * Useful for mobile apps or when env vars aren't available.
 */
export function setSparkLatticeUrl(url) {
    globalThis.__SPARK_LATTICE_URL__ = url;
}
/**
 * Shared Spark lattice URL (use getSparkLatticeUrl() for dynamic access).
 * This is kept for backwards compatibility but prefer the function.
 *
 * @deprecated Use getSparkLatticeUrl() instead for proper env var support
 */
export const SPARK_LATTICE_URL = DEFAULT_SPARK_LATTICE_URL;
/**
 * Spark API base path
 */
export const SPARK_API_PATH = '/api/auth';
/**
 * Get Spark API endpoints for the configured lattice URL
 */
export function getSparkEndpoints(latticeUrl) {
    const baseUrl = latticeUrl ?? getSparkLatticeUrl();
    return {
        PENDING_CHALLENGES: `${baseUrl}${SPARK_API_PATH}/pending-challenges`,
        SPARK_VERIFY: `${baseUrl}${SPARK_API_PATH}/spark-verify`,
        SPARK_STATUS: `${baseUrl}${SPARK_API_PATH}/spark-status`,
    };
}
/**
 * Spark API endpoints (using default URL).
 * For dynamic URL support, use getSparkEndpoints() instead.
 *
 * @deprecated Use getSparkEndpoints() for proper env var support
 */
export const SPARK_ENDPOINTS = {
    PENDING_CHALLENGES: `${DEFAULT_SPARK_LATTICE_URL}${SPARK_API_PATH}/pending-challenges`,
    SPARK_VERIFY: `${DEFAULT_SPARK_LATTICE_URL}${SPARK_API_PATH}/spark-verify`,
    /** Session status - append session ID */
    SPARK_STATUS: `${DEFAULT_SPARK_LATTICE_URL}${SPARK_API_PATH}/spark-status`,
};
/**
 * Get the session status URL for a specific session
 */
export function getSparkStatusUrl(sessionId, latticeUrl) {
    const endpoints = getSparkEndpoints(latticeUrl);
    return `${endpoints.SPARK_STATUS}/${sessionId}`;
}
/**
 * Compatible apps in the eStream ecosystem
 */
export const ECOSYSTEM_APPS = {
    estream: {
        id: 'estream',
        name: 'eStream',
        actionPrefix: 'io.estream/',
        consoleUrl: 'https://console.estream.dev',
    },
    taketitle: {
        id: 'taketitle',
        name: 'TakeTitle',
        actionPrefix: 'io.taketitle/',
        consoleUrl: 'https://taketitle.io',
    },
    polymessenger: {
        id: 'polymessenger',
        name: 'PolyMessenger',
        actionPrefix: 'app.polymessenger/',
        consoleUrl: 'https://polymessenger.app',
    },
};
/**
 * Check if an action is allowed for a given app scope
 */
export function isActionAllowed(action, appScope) {
    switch (appScope.toLowerCase()) {
        case 'global':
        case 'estream-app':
            return (action.startsWith('io.estream/') ||
                action.startsWith('io.taketitle/') ||
                action.startsWith('app.polymessenger/'));
        case 'estream':
        case 'estream-console':
            return action.startsWith('io.estream/');
        case 'taketitle':
            return action.startsWith('io.taketitle/') || action.startsWith('io.estream/');
        case 'polymessenger':
            return action.startsWith('app.polymessenger/') || action.startsWith('io.estream/');
        default:
            return false;
    }
}
// ============================================================================
// Privacy Guarantees Documentation
// ============================================================================
/**
 * What's stored on the Spark lattice (public, but privacy-preserving):
 *
 * ```
 * SparkChallengeEvent {
 *   challenge_id: string;    // Random, ephemeral (5 min TTL)
 *   session_id: string;      // Random, per-session
 *   display_code: string;    // First 8 chars of challenge_id
 *   expires_at: number;      // Public timestamp only
 *   encrypted_payload: string; // ML-KEM encrypted, viewer-specific
 * }
 * ```
 *
 * What an observer CANNOT learn:
 * - User identity (no public keys until after verification)
 * - Cross-service correlation (different session IDs)
 * - Activity patterns (challenges are ephemeral)
 * - Action details (encrypted with viewer's key)
 *
 * Privacy is achieved through:
 * 1. Ephemeral challenge IDs (deleted after 5 min)
 * 2. Random session IDs (no correlation)
 * 3. Encrypted payloads (ML-KEM)
 * 4. No public keys on lattice (only hashes after verify)
 */
export const SPARK_PRIVACY_GUARANTEES = {
    challengeTtl: 5 * 60 * 1000, // 5 minutes
    nonceTtl: 5 * 60 * 1000, // 5 minutes (matches challenge)
    sessionTtl: 24 * 60 * 60 * 1000, // 24 hours
    payloadEncryption: 'ML-KEM-1024',
    signatureAlgorithm: 'ML-DSA-87',
};
//# sourceMappingURL=spark-enhanced.js.map